import { useMemo } from 'react';

import { usePageContext } from './usePageContext';

const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  weekday: 'short',
};

const DEFAULT_DATE_OPTIONS_WITH_YEAR: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  weekday: 'short',
  year: 'numeric',
};

export function useLocale() {
  const pageContext = usePageContext();

  return useMemo(() => {
    // Fun, Cloudflare Workers actually has `navigator`
    const locale =
      ('headers' in pageContext && pageContext.headers?.['accept-language']?.split(',')[0]) ||
      ('request' in pageContext &&
        pageContext.request?.headers?.['accept-language']?.split(',')[0]) ||
      (typeof navigator !== 'undefined' && navigator?.language) ||
      'en-US';

    const formatDate = (
      date: number | string | Date | undefined,
      options?: Intl.DateTimeFormatOptions,
    ) => {
      const now = new Date();
      const dateObj = date instanceof Date ? date : date ? new Date(date) : now;
      const optObj =
        dateObj.getFullYear() !== now.getFullYear()
          ? DEFAULT_DATE_OPTIONS_WITH_YEAR
          : DEFAULT_DATE_OPTIONS;

      return new Intl.DateTimeFormat(locale, options ? { ...optObj, ...options } : optObj).format(
        dateObj,
      );
    };

    const formatNumber = (value: number, options?: Intl.NumberFormatOptions) =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
        ...options,
      }).format(value);

    return { formatDate, formatNumber, locale };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
